import { VisualizationConfig, VisualizationType } from "./dataVisualizer"

/**
 * 解析后的思考步骤数据结构
 */
export interface ParsedThinkingData {
  title: string;
  detail: any;
  results: any;
  searchKeywords: string[];
  searchResults: Array<{ domain: string; title: string; url?: string }>;
  resultsCount: number;
  visualizationType?: any
}

/**
 * 解析思考过程的数据
 * 目前主要处理 Web Search，未来可扩展其他类型
 */
export function parseThinkingData(
  message: string,
  userFriendlyInfo?: any,
  thinkingContent?: any,
  visualization?: any,
): ParsedThinkingData {
  const title = message;
  let detail: any = userFriendlyInfo?.details ? userFriendlyInfo?.details : thinkingContent?.reasoning;
  let results: any = userFriendlyInfo?.results ?? userFriendlyInfo?.result;
  let searchKeywords: string[] = [];
  let searchResults: Array<{ domain: string; title: string }> = [];
  let resultsCount = 0;
  let visualizationType = 'markdown_narrative';

  if (isWebSearch(title)) {
    // Web Search 特殊处理
    if (detail) {
      const searchMatch = detail.match(/^Searching for:\s*(.+?)(?:\.\.\.|$)/);
      if (searchMatch) {
        // 分割搜索关键词，按逗号分割
        const keywordsString = searchMatch[1];
        searchKeywords = keywordsString
          .split(',')
          .map((k: any) => k.trim())
          .filter((k: any) => k.length > 0)
          .slice(0, 5); // 最多显示5个关键词

        // 清空原始 detail，因为我们要用新格式显示
        detail = null;
      }
    }

    // 解析 Web Search 结果
    if (results) {
      // 首先尝试解析 JSON 格式
      if (Array.isArray(results)) {
        // 直接是数组格式
        searchResults = results.map(item => ({
          domain: item.domain || '',
          title: item.title || '',
          url: item.url || ''
        }));
        resultsCount = searchResults.length;

        // 成功解析，清空原始 results
        if (searchResults.length > 0) {
          results = null;
        }
      } else if (typeof results === 'string') {
        // 尝试解析为 JSON 字符串
        try {
          const parsedResults = JSON.parse(results);
          // console.log('parsedResults',parsedResults)
          if (Array.isArray(parsedResults)) {
            searchResults = parsedResults.map(item => ({
              domain: item?.metadata?.url || '',
              title: item?.metadata?.title || '',
              url: item?.metadata?.url || ''
            }));
            resultsCount = searchResults.length;

            // 成功解析，清空原始 results
            if (searchResults.length > 0) {
              results = null;
            }
          }
        } catch (e) {
          // JSON 解析失败，尝试文本格式解析
          // 解析 "Found X websites:" 或 "Found X results from:" 格式
          const countMatch = results.match(/Found (\d+) (?:websites?|results?(?: from)?):?/i);
          if (countMatch) {
            resultsCount = parseInt(countMatch[1]);

            // 解析每一行网站信息
            const lines = results.split('\n');
            lines.forEach((line: any) => {
              // 匹配格式：• domain - title
              const siteMatch = line.match(/^•\s*([^\s]+)\s*-\s*(.+)$/);
              if (siteMatch) {
                searchResults.push({
                  domain: siteMatch[1],
                  title: siteMatch[2]
                });
              }
            });

            // 如果成功解析，清空原始 results
            if (searchResults.length > 0) {
              results = null;
            }
          }
        }
      }
    }
  } else if (isToolExecution(title)) {
    visualizationType = 'tool_execution';
    try {
      results = JSON.parse(results);
      if (!results?.type) {
        visualizationType = 'markdown_narrative';
        results = null;
      }
    } catch (error) {

    }
  } else {
    if (visualization?.type) {
      //RenderingMode
      results = visualization.config;
      visualizationType = visualization?.type;
    } else {
      results = null;
    }
  }

  return {
    title,
    detail,
    results,
    searchKeywords,
    searchResults,
    resultsCount,
    visualizationType,
  };
}

/**
 * 判断是否有可展开的内容
 */
export function hasExpandableContent(data: ParsedThinkingData): boolean {
  return !!(
    data.detail ||
    data.results ||
    data.searchKeywords.length > 0 ||
    data.searchResults.length > 0
  );
}

/**
 * 判断是否为 Web Search 类型
 */
export function isWebSearch(title: string): boolean {
  return title === 'Web Search';
}

export function isToolExecution(title: string): boolean {
  return title === 'Tool Execution';
}

/**
 * 判断解析的数据是否包含 Web Search 特殊格式内容
 */
export function hasWebSearchContent(data: ParsedThinkingData): boolean {
  return data.searchKeywords.length > 0 || data.searchResults.length > 0;
}
